import { ReactNode, useEffect, useState } from "react";
import {
  App,
  Button,
  Card,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  ModalProps,
  Image,
  Spin
} from "antd";
import { useTranslation } from "react-i18next";
import apis from "@/apis/purchase";
import { docFetch } from "@/apis/doc";
import { getFileUrl } from "@/apis/file"
import { Supplier, SupplierPrice, SupplierPriceAdd } from "@/types/purchase";
import { ProductSelect } from "@/components/Selector";
import useDataDetail from "@/hooks/useDataDetail";
import dayjs from "dayjs";
import { CloseOutlined, ExclamationCircleFilled } from "@ant-design/icons";
import ProductDesc from "@/components/Desc/ProductDesc";
import { divided, noRateAmount } from "@/utils/calc";
import { priceFormat } from "@/utils/format";
import { CurrencySelect } from "@/components/Selector";
import { Product } from "@/types/product";
import AddressSelect from "@/layout/HomeLayout/components/AddressSelect";

interface PriceFormProps extends ModalProps {
  handleCancel: () => void;
  handleOk: (data: SupplierPriceAdd) => Promise<void>;
  id: string;
  type: "add" | "update";
  leftTitle: ReactNode;
  confirmLoading: boolean;
  isSupplierPage?: boolean;
  propProductList?: Product[];
  disabled?: boolean;
  supplier?: Supplier;
}

const PriceForm = ({
  id,
  type,
  leftTitle,
  handleOk,
  handleCancel,
  isSupplierPage = false,
  propProductList,
  disabled,
  supplier,
  ...props
}: PriceFormProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<SupplierPriceAdd>();
  const selectedAddress = Form.useWatch("address", form);
  const priceValue = Form.useWatch("price", form);
  const taxRateValue = Form.useWatch("taxRate", form);
  const { notification, modal } = App.useApp();
  const [picUrl, setPicUrl] = useState<string | undefined>('')
  const [spinning, setSpinning] = useState<boolean>(false)

  const {
    loading: deLoading,
    fetchDetail: fetch,
    detail,
  } = useDataDetail<SupplierPrice, [string]>({ api: apis.priceFetch });

  useEffect(() => {
    type === "update" && !isSupplierPage &&
      id &&
      fetch(id).then((res) =>
        form.setFieldsValue({
          ...res,
          ladders: res.ladders?.length > 1 ? res.ladders?.splice(1) : [],
          taxRate: res?.taxRate ?? "",
        })
      );
  }, []);

  useEffect(() => {
    if (propProductList && propProductList.length > 0) {
      form.setFieldValue("productId", propProductList[0].id)
    }
  }, [propProductList])

  const taxRate = taxRateValue || supplier?.taxRate;
  const untaxedUnitPrice = taxRate ? noRateAmount(
    priceValue,
    divided(taxRate, "100"),
  ) : "";

  const handleFinish = () => {
    form
      .validateFields()
      .then((res) => {
        const value: SupplierPriceAdd = {
          ...res,
          price: res.price.toString(),
          effectiveAt: res.effectiveAt
            ? res.effectiveAt
            : 0,
          expiredAt: res.expiredAt
            ? res.expiredAt
            : 0,
          ladders: [
            {
              price: res.price.toString(),
              count: 1,
              remark: res.remark,
              discount: "100",
            },
            ...(res?.ladders?.map((ladder) => ({
              price: (ladder?.price ?? 0).toString(),
              count: ladder?.count ?? 0,
              remark: ladder?.remark ?? "",
              discount: Number(divided(ladder.price, res.price)).toFixed(2),
            })) ?? []),
          ],
        };
        handleOk(value).then(() => {
          form.resetFields();
        })
          .catch((err) => {
            console.error('提交失败:', err);
            notification.error({
              placement: 'top',
              message: t("common.addError"),
              description: err?.message || t("common.serverError"),
            });
          });
      })
      .catch((err) => {
        console.error('表单验证失败:', err);
        const errTitle = t("common.addError");
        const errorDesc = err?.errorFields?.map((field: any) => {
          return `${field.name[0]}: ${field.errors.join(', ')}`;
        }).join('\n') || err?.errorFields[0]?.errors[0] || "";

        notification.error({
          placement: 'top',
          message: errTitle,
          description: errorDesc,
        });
      });
  };

  const onCancel = () => {
    form.resetFields();
    setPicUrl('')
    handleCancel();
  };

  const handlePic = (product: Product | null) => {
    if (product) {
      let docId = product.picDocId
      setSpinning(true)
      docFetch(docId ?? '').then((doc) => {
        let pic: string | undefined = ''
        for (let i = 0; i < doc.subs.length; i++) {
          if (doc.subs[i].fileId) {
            pic = getFileUrl(doc.subs[i].fileId)
            break
          }
        }
        setPicUrl(pic)
      })
        .catch(() => undefined)
        .finally(() => setSpinning(false))
    }
  };

  return (
    <Modal
      centered
      loading={deLoading}
      width={800}
      onOk={handleFinish}
      onCancel={onCancel}
      maskClosable={false}
      title={type === "add" ? t("common.add") : t("common.edit")}
      {...props}
    >
      <div style={{ maxHeight: "70vh", overflow: "scroll" }}>
        <Form<SupplierPriceAdd>
          form={form}
          name="priceForm"
          autoComplete="off"
          initialValues={{
            price: detail?.price ?? "0",
            netWeight: null,
            grossWeight: null,
            effectiveAt: type === "add" ? dayjs().valueOf() : undefined,
            expiredAt: type === "add" ? dayjs().add(1, 'year').valueOf() : undefined,
          }}
        >
          {type === "add" &&
            <div style={{ position: 'absolute', right: 40, top: 50, zIndex: 2 }}>
              <Spin spinning={spinning}>
                <Image
                  style={{ width: 100, height: 100 }}
                  src={picUrl}
                  fallback="data:image/png;base64,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"
                />
              </Spin>
            </div>
          }

          <Form.Item
            label={t("purchase.product")}
            hidden={type === "update"}
            name="productId"
            required
            rules={[{ required: true, message: t("components.plProduct") }]}
          >
            <ProductSelect width={'80%'} defaultList={propProductList}
              getSelectPro={handlePic}
            />
          </Form.Item>
          <Form.Item label={t("purchase.product")} hidden={type === "add"}>
            <ProductDesc product={detail?.product ?? null} />
          </Form.Item>
          <Form.Item
            label={t("purchase.stPrice")}
            name="price"
            required
            rules={[{ required: true, message: t("purchase.plPrice") }]}
          >
            <InputNumber changeOnWheel
              formatter={(value) => priceFormat(value || '')}
              stringMode
              min={0}
              precision={10000}
              placeholder={t("purchase.plPrice")}
              style={{ width: type === "add" ? "80%" : '100%' }}
              disabled={disabled}
            />
          </Form.Item>
          <Form.Item label={t("purchase.taxRate")} name="taxRate" initialValue={supplier?.taxRate ?? ''}>
            <InputNumber changeOnWheel
              stringMode
              min={0}
              max={100}
              precision={2}
              placeholder={t("purchase.plTaxRate")}
              style={{ width: type === "add" ? "80%" : '100%' }}
            />
          </Form.Item>
          <Form.Item label={t("purchase.unRatePrice")}>
            <Input
              value={untaxedUnitPrice ? priceFormat(untaxedUnitPrice) : ""}
              readOnly
            />
          </Form.Item>
          <Form.Item
            label={t("purchase.effectiveAt")}
            name="effectiveAt"
            getValueProps={(value) => ({
              value: value ? (typeof value === 'number' ? dayjs(value) : value) : undefined,
            })}
            normalize={(value) => value ? dayjs(value).valueOf() : undefined}
          >
            <DatePicker
              style={{ width: type === "add" ? "80%" : '100%' }}
              placeholder={t("purchase.effectiveAtPl")}
              disabled={disabled}
            />
          </Form.Item>
          <Form.Item
            label={t("purchase.expiredAtEcl")}
            name="expiredAt"
            getValueProps={(value) => ({
              value: value ? (typeof value === 'number' ? dayjs(value) : value) : undefined,
            })}
            normalize={(value) => value ? dayjs(value).valueOf() : undefined}
          >
            <DatePicker
              style={{ width: type === "add" ? "80%" : '100%' }}
              placeholder={t("purchase.expiredAtEclPl")}
              disabled={disabled}
            />
          </Form.Item>
          <Form.Item label={t("purchase.days")} name="days"
          >
            <InputNumber changeOnWheel disabled={disabled} style={{ width: "100%" }} placeholder={t("purchase.plDays")} precision={0} min={0} />
          </Form.Item>
          <Form.Item label={t("purchase.mpq")} name="mpq">
            <InputNumber changeOnWheel disabled={disabled} style={{ width: "100%" }} placeholder={t("purchase.plMpq")} precision={0} min={1} stringMode />
          </Form.Item>
          <Form.Item label={t("purchase.brand")} name="brand">
            <Input
              disabled={disabled}
              placeholder={t("purchase.plBrand")}
            />
          </Form.Item>
          <Form.Item label={t("purchase.netWeight")} name="netWeight">
            <InputNumber
              disabled={disabled}
              placeholder={t("purchase.plNetWeight")}
              min={0}
              addonAfter="kg"
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item label={t("purchase.grossWeight")} name="grossWeight">
            <InputNumber
              disabled={disabled}
              placeholder={t("purchase.plGrossWeight")}
              min={0}
              addonAfter="kg"
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item label={t("purchase.mpn")} name="mpn">
            <Input
              disabled={disabled}
              placeholder={t("purchase.plMpn")}
            />
          </Form.Item>
          <Form.Item label="D/C" name="dcc">
            <Input
              disabled={disabled}
              style={{ width: "100%" }}
              placeholder="D/C"
            />
          </Form.Item>
          <Form.Item label={t("purchase.currency")} name="currency" initialValue={type === "add" ? supplier?.currency : ''}
            required
            rules={[{ required: true, message: t("components.plCurrency") }]}
          >
            <CurrencySelect readOnly={disabled || !!selectedAddress} />
          </Form.Item>
          <Form.Item label={t("purchase.sendAds")} name="address"
            required
            rules={[{ required: true, message: t("purchase.sendAdsPl") }]}
          >
            <AddressSelect onChange={(_, data) => form.setFieldsValue({ currency: data?.label1 })} />
          </Form.Item>
          <Form.Item label={t("purchase.spareRate_1")} name="spareRate"
          >
            <InputNumber changeOnWheel disabled={disabled} style={{ width: "100%" }} placeholder={t("purchase.spareRatePl")} precision={2} min={'0'} max={'100'} controls={false} addonAfter='%' />
          </Form.Item>
          <Form.Item
            label={t("purchase.remark")}
            name="remark"
            rules={[
              {
                max: 1000,
                message: t("purchase.plRemarkMax", { length: 1000 }),
              },
            ]}
          >
            <Input.TextArea
              autoSize={{ minRows: 2, maxRows: 4 }}
              placeholder={t("purchase.plRemark")}
              maxLength={1000}
              showCount
              disabled={disabled}
            />
          </Form.Item>
          <Form.List name="ladders">
            {(fields, { add, remove }) => (
              <Flex vertical gap={10}>
                {fields.map((field, index) => (
                  <Card
                    size="small"
                    title={t("purchase.priceIndex", { index: index + 1 })}
                    key={field.key}
                    extra={
                      <CloseOutlined
                        onClick={() => {
                          const value = form.getFieldValue("ladders");
                          if (
                            value &&
                            value[index] &&
                            (value[index].count || value[index].price)
                          ) {
                            modal.confirm({
                              title: (
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: t("common.rmTip"),
                                  }}
                                />
                              ),
                              icon: <ExclamationCircleFilled />,
                              content: (
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: t("common.rmContent"),
                                  }}
                                />
                              ),
                              cancelText: t("common.cancel"),
                              okText: t("common.confirm"),
                              onOk: () => remove(field.name),
                            });
                          } else {
                            remove(field.name);
                          }
                        }}
                      />
                    }
                  >
                    <Form.Item
                      label={t("purchase.priceCount")}
                      name={[field.name, "count"]}
                      required
                      rules={[
                        {
                          required: true,
                          message: t("purchase.plPriceCountIndex", {
                            index: index + 1,
                          }),
                        },
                      ]}
                    >
                      <InputNumber changeOnWheel
                        precision={0}
                        min={0}
                        placeholder={t("purchase.plPriceCount")}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                    <Form.Item
                      label={t("purchase.stPrice")}
                      name={[field.name, "price"]}
                      required
                      rules={[
                        {
                          required: true,
                          message: t("purchase.plPriceItemIndex", {
                            index: index + 1,
                          }),
                        },
                      ]}
                    >
                      <InputNumber changeOnWheel
                        stringMode
                        min={0}
                        placeholder={t("purchase.plPriceIndex")}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                    <Form.Item
                      label={t("purchase.remark")}
                      name={[field.name, "remark"]}
                    >
                      <Input.TextArea placeholder={t("purchase.plRemark")} />
                    </Form.Item>
                  </Card>
                ))}
                <Button type="dashed" onClick={() => add()} block>
                  {t("purchase.plPriceAdd")}
                </Button>
              </Flex>
            )}
          </Form.List>
        </Form>
      </div>
    </Modal>
  );
};

export default PriceForm;
